import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { listMyStudentTCs, listMyStudents } from "@/lib/api/parent-invites.functions";
import { listMyStoreCatalog, placeMyStoreOrder } from "@/lib/api/parent-portal.functions";
import { paymentMethodLabel } from "@/lib/payment-methods";

export const Route = createFileRoute("/_authenticated/responsavel/loja")({
  head: () => ({
    meta: [
      { title: "Loja | FutBux" },
      { name: "description", content: "Produtos e uniformes do seu Centro de Treinamento." },
      { property: "og:title", content: "Loja | FutBux" },
      { property: "og:description", content: "Produtos e uniformes do seu Centro de Treinamento." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const ACTIVE_KEY = "futbux:activeParentTc";
const fmtBRL = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function Page() {
  const navigate = useNavigate();
  const listTcs = useServerFn(listMyStudentTCs);
  const listStudentsFn = useServerFn(listMyStudents);
  const listCatalog = useServerFn(listMyStoreCatalog);
  const placeOrder = useServerFn(placeMyStoreOrder);

  const [tcId, setTcId] = useState<string | null>(null);
  const [tcName, setTcName] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [uniforms, setUniforms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [pick, setPick] = useState<null | { kind: "product" | "uniform"; item: any }>(null);
  const [studentId, setStudentId] = useState("");
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState("");
  const [pay, setPay] = useState("");
  const [jName, setJName] = useState("");
  const [jNumber, setJNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");


  useEffect(() => {
    (async () => {
      const stored = (() => { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } })();
      const tcsRes = await listTcs();
      const tcs = tcsRes.tcs ?? [];
      if (tcs.length === 0) { setLoading(false); return; }
      const active = tcs.find((t: any) => t.training_center_id === stored) ?? tcs[0];
      if (tcs.length > 1 && !stored) { navigate({ to: "/selecionar-ct" }); return; }
      setTcId(active.training_center_id); setTcName(active.tc_name);
      const [studs, cat] = await Promise.all([
        listStudentsFn(),
        listCatalog({ data: { tcId: active.training_center_id } }),
      ]);
      setStudents((studs.students ?? []).filter((s: any) => s.training_center_id === active.training_center_id));
      setProducts(cat.products ?? []);
      setUniforms(cat.uniforms ?? []);
      setLoading(false);
    })();
  }, []);

  const openBuy = (kind: "product" | "uniform", item: any) => {
    setPick({ kind, item }); setStudentId(""); setQty(1); setSize(""); setPay(""); setJName(""); setJNumber("");
  };

  const submit = async () => {
    if (!pick || !tcId || !pay) return;
    setSaving(true);
    try {
      const res = await placeOrder({
        data: {
          tcId, kind: pick.kind, itemId: pick.item.id,
          studentId: studentId || null, quantity: qty,
          size: size || null, paymentMethod: pay,
          jerseyName: jName || null, jerseyNumber: jNumber || null,
        },
      });
      toast.success("Pedido registrado! Aguarde a confirmação do Centro.");
      setPick(null);
      if (res.channel === "comprovante") navigate({ to: "/responsavel/financeiro" });
    } catch (e: any) {
      toast.error(e.message ?? "Erro ao registrar pedido");
    } finally { setSaving(false); }
  };

  const filterList = (arr: any[]) => q ? arr.filter((x) => (x.name ?? "").toLowerCase().includes(q.toLowerCase())) : arr;

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  if (!tcId) return <div className="p-6 text-sm text-muted-foreground">Nenhum Centro vinculado.</div>;

  const methods: string[] = pick
    ? (pick.item.accepted_payment_methods ?? [])
    : [];
  const uniformSizes: any[] = pick?.kind === "uniform" ? (pick.item.uniform_sizes ?? []) : [];


  return (
    <div className="p-4 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Loja</h1>
        <p className="text-muted-foreground text-sm">{tcName}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input className="max-w-sm h-9" placeholder="Buscar produto ou uniforme…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-foreground/80">Uniformes</h2>
        {filterList(uniforms).length === 0 ? <p className="text-sm text-muted-foreground">Sem uniformes disponíveis.</p> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filterList(uniforms).map((u) => (
              <Card key={u.id}>
                {u.photo_url && <img src={u.photo_url} alt={u.name} className="h-40 w-full object-cover" />}
                <CardHeader className="pb-2"><CardTitle className="text-base">{u.name}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>{fmtBRL(Number(u.price ?? 0))}</div>
                  <Button size="sm" onClick={() => openBuy("uniform", u)}><ShoppingBag className="h-4 w-4 mr-1" /> Comprar</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-foreground/80">Produtos</h2>
        {filterList(products).length === 0 ? <p className="text-sm text-muted-foreground">Sem produtos disponíveis.</p> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filterList(products).map((p) => (
              <Card key={p.id}>
                {p.photo_url && <img src={p.photo_url} alt={p.name} className="h-40 w-full object-cover" />}
                <CardHeader className="pb-2"><CardTitle className="text-base">{p.name}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {p.description && <p className="text-muted-foreground line-clamp-2">{p.description}</p>}
                  <div>{fmtBRL(Number(p.price ?? 0))}</div>
                  <Button size="sm" onClick={() => openBuy("product", p)}><ShoppingBag className="h-4 w-4 mr-1" /> Comprar</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>


      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{pick?.item?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {pick?.kind === "uniform" && (
              <>
                <div>
                  <Label>Tamanho</Label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {uniformSizes.map((s) => <SelectItem key={s.id} value={s.size}>{s.size}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {pick.item.require_jersey_name && (
                  <div><Label>Nome na camisa</Label><Input value={jName} onChange={(e) => setJName(e.target.value)} /></div>
                )}
                {pick.item.require_jersey_number && (
                  <div><Label>Número da camisa</Label><Input value={jNumber} onChange={(e) => setJNumber(e.target.value)} /></div>
                )}
              </>
            )}
            <div>
              <Label>Aluno (opcional)</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger><SelectValue placeholder="Compra avulsa" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantidade</Label>
              <Input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} />
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={pay} onValueChange={setPay}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {methods.map((m) => <SelectItem key={m} value={m}>{paymentMethodLabel(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md bg-muted p-2 text-sm">
              <span>Total:</span>
              <b>{fmtBRL(Number(pick?.item?.price ?? 0) * qty)}</b>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPick(null)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving || !pay}>{saving ? "Enviando…" : "Confirmar pedido"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
